import { UseMutationResult } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch, SetStateAction, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useStagingSitePromote } from '@newfold/huapi-js';
import {
  StagingSitePromote200,
  StagingSitePromote409,
  StagingSitePromoteBody,
} from '@newfold/huapi-js/src/index.schemas';

import { useAlert } from '~/components/Alerts/alertsStore';

import { useBrandInfo } from './useBrandInfo';

export type SitePromoteMutationResponseType = UseMutationResult<
  AxiosResponse<StagingSitePromote200, any>,
  AxiosError<StagingSitePromote409, any>,
  {
    siteId: number;
    data: StagingSitePromoteBody;
  },
  unknown
>;

type PromoteSiteValidateOnlyResponseTypes = {
  openPromoteSiteModal: boolean;
  setOpenPromoteSiteModal: Dispatch<SetStateAction<boolean>>;
  handleSitePromote: (siteId: number) => void;
  sitePromoteResp?: SitePromoteMutationResponseType;
};

const usePromoteSiteValidateOnly = (): PromoteSiteValidateOnlyResponseTypes => {
  const { t } = useTranslation('cloud', {
    keyPrefix: 'staging.promoteSite',
  });

  const { phoneNumberFormatted } = useBrandInfo();

  const generateAlert = useAlert();

  const [openPromoteSiteModal, setOpenPromoteSiteModal] = useState(false);

  const sitePromoteResp = useStagingSitePromote({
    mutation: {
      onSuccess: () => {
        setOpenPromoteSiteModal(true);
      },
      onError: (error) => {
        if (error?.response?.data?.error === 'resourceUsageExceeded') {
          setOpenPromoteSiteModal(true);
        } else {
          return generateAlert({
            severity: 'error',
            description: (
              <Trans
                i18nKey={t('alertMessage.error')}
                values={{ phoneNumber: phoneNumberFormatted }}
              />
            ),
            showCloseBtn: true,
            persist: true,
          });
        }
      },
    },
  });

  const handleSitePromote = (siteId: number) => {
    sitePromoteResp.mutate({
      siteId,
      data: { validate_only: true },
    });
  };

  return {
    openPromoteSiteModal,
    setOpenPromoteSiteModal,
    handleSitePromote,
    sitePromoteResp,
  } as PromoteSiteValidateOnlyResponseTypes;
};

export default usePromoteSiteValidateOnly;
